import React from 'react';
import { View } from 'react-native';
import { Image } from 'expo-image';
import { useQuery } from '@tanstack/react-query';

import { Text } from '@/components/gluestack/text';
import { getKlipyGifQueryOptions } from '@/components/ui/media/providers/klipy/klipyGifQuery';
import {
  getKlipyGifAspectRatio,
  getKlipyGifPreview,
} from '@/components/ui/media/providers/klipy/klipyGifUtils';
import { useKlipyApiKey } from '@/hooks/useKlipyApiKey';
import { useTranslation } from 'react-i18next';

const DEFAULT_MAX_WIDTH = 240;
const COMPACT_PREVIEW_SIZE_THRESHOLD = 160;

interface IKlipyGifViewProps {
  slug: string;
  rounded?: boolean;
  maxWidth?: number;
}

export function KlipyGifView({
  slug,
  rounded = true,
  maxWidth = DEFAULT_MAX_WIDTH,
}: IKlipyGifViewProps) {
  const roundingClass = rounded ? 'rounded-xl' : '';
  const { t } = useTranslation();
  const apiKey = useKlipyApiKey();

  const { data: gif, isPending, isError } = useQuery(getKlipyGifQueryOptions(apiKey, slug));

  const previewSize = maxWidth <= COMPACT_PREVIEW_SIZE_THRESHOLD ? 'sm' : 'md';
  const preview = gif ? getKlipyGifPreview(gif, previewSize) : undefined;
  const aspectRatio = gif ? getKlipyGifAspectRatio(gif, previewSize) : 1;
  const displayWidth = preview?.width ? Math.min(preview.width, maxWidth) : maxWidth;
  const displayHeight = displayWidth / aspectRatio;
  const accessibilityLabel = gif?.title?.trim() || undefined;

  if (isPending) {
    return (
      <View
        className={`overflow-hidden bg-background-200 ${roundingClass}`}
        style={{ width: displayWidth, height: displayHeight }}
      />
    );
  }

  if (isError || !preview?.url) {
    return (
      <View
        className={`items-center justify-center bg-background-100 px-3 py-4 ${roundingClass}`}
        style={{ width: displayWidth, minHeight: 96 }}>
        <Text size="sm" className="text-typography-500">
          {t('media.failed_to_load')}
        </Text>
      </View>
    );
  }

  return (
    <View className={`overflow-hidden ${roundingClass}`} style={{ width: displayWidth }}>
      <Image
        source={{ uri: preview.url }}
        style={{ width: displayWidth, height: displayHeight }}
        contentFit="cover"
        recyclingKey={preview.url}
        accessibilityLabel={accessibilityLabel}
      />
    </View>
  );
}
